import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./IntegrationsPage.module.css";
import HeaderBar from "../HeaderBar/HeaderBar";
import Tooltip from "../TooltipSteps/Tooltip";
import decodeToken from "../../lib/decodeToken";



const API_BASE = process.env.REACT_APP_API_BASE_URL; // e.g. https://.../api


const getHubspotAuthUrl = (userId) =>
  // Pass the current frontend origin so the OAuth callback returns the user to
  // the SAME domain (.in / .ai) they connected from (validated server-side).
  `${API_BASE}/integration/auth/hubspot?state=${encodeURIComponent(
    userId,
  )}&origin=${encodeURIComponent(window.location.origin)}`;


const GUIDE_URLS = {
  zapier: "https://rexpt-main-website-dev-server.vercel.app/zapier-integration-guide",
  make: "https://rexpt-main-website-dev-server.vercel.app/make-integration-guide",
};


const HELP_CONTENT = {
  shopify: (
    <span>
      <strong>How to connect Shopify:</strong>
      <br />
      1. Click <strong>Connect</strong> and enter your{" "}
      <strong>.myshopify.com</strong> store address.
      <br />
      2. Approve the install on Shopify.
      <br />
      3. You’ll return here and the status becomes “Connected”.
      <br />
      <br />
      Note: Shopify connects at the <strong>account level</strong> for your whole
      Rexpt account.
    </span>
  ),
  hubspot: (
    <span>
      <strong>How to set up HubSpot:</strong>
      <br />
      1. Click <strong>Connect</strong> — you’ll be sent to HubSpot to log in.
      <br />
      2. Choose your HubSpot account and click <strong>Allow</strong>.
      <br />
      3. You’ll come back here and the status becomes “Connected”.
      <br />
      <br />
      Note: HubSpot connects at the <strong>account level</strong> (for your whole
      Rexpt account), not per agent — so it applies to all your agents.
    </span>
  ),
};

const PLATFORMS = [
  {
    key: "mcp",
    name: "MCP Servers",
    description: "Connect Monday, HubSpot & other MCP tools your agent can call.",
    icon: "/svg/mcp_icon.svg",
    status: "available",
  },
 
  {
    key: "make",
    name: "Make.com",
    description: "Automate workflows across 3,000+ apps with your agent.",
    icon: "/svg/make.svg",
    status: "available",
  },
  {
    key: "zapier",
    name: "Zapier",
    description: "Connect your agent to 9,000+ apps with Zaps.",
    icon: "/svg/zapier.svg",
    status: "available",
  },
  {
    key: "hubspot",
    name: "HubSpot",
    description: "Push call data and contacts straight into your CRM.",
    icon: "/svg/hubspot.svg",
    status: "available",
  },
  // {
  //   key: "zoho",
  //   name: "Zoho",
  //   description: "Send leads and call activity into Zoho CRM.",
  //   icon: "/svg/zoho.svg",
  //   status: "coming_soon",
  // },
  // {
  //   key: "clover",
  //   name: "Clover",
  //   description: "Link your POS to keep bookings and payments in sync.",
  //   icon: "/svg/clover.svg",
  //   status: "coming_soon",
  // },
  // {
  //   key: "square",
  //   name: "Square",
  //   description: "Connect Square POS for payments and appointments.",
  //   icon: "/svg/square.svg",
  //   status: "coming_soon",
  // },
];


const SECTIONS = [
  
  {
    title: "Developer & MCP",
    subtitle: "Connect developer tools and MCP servers your agent can call.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M8 8l-4 4 4 4M16 8l4 4-4 4M13 5l-2 14" stroke="#6524EB" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    keys: ["mcp"],
  },
  {
    title: "Direct Integrations",
    subtitle: "Connect essential tools directly with Rexpt AI.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M12 2l8 4.5v9L12 20l-8-4.5v-9L12 2z" stroke="#6524EB" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M12 2v9m0 0l8-4.5M12 11L4 6.5" stroke="#6524EB" strokeWidth="1.7" strokeLinejoin="round" />
      </svg>
    ),
    keys: ["hubspot"],
  },
  
  {
    title: "Automation Platforms",
    subtitle: "Connect to automation platforms and extend your workflows.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" fill="#6524EB" />
      </svg>
    ),
    keys: ["make", "zapier"],
  }
];

const IntegrationsPage = () => {
  const userId = decodeToken(localStorage.getItem("token") || "")?.id || "";

  // This page is opened FOR a specific agent (FooterNew passes it via nav state).
  // Make/Zapier are per-agent → we send this agentId on status + disconnect so
  // the backend checks/disconnects only THIS agent. HubSpot is user-level and
  // never receives agentId.
  const location = useLocation();
  const navigate = useNavigate();
  const agentId = location.state?.agentId || null;
  // Outbound Caller (Pounce) agent ke liye sirf MCP dikhta hai.
  const mcpOnly = location.state?.mcpOnly === true;
  const sections = mcpOnly ? SECTIONS.filter((s) => s.keys.includes("mcp")) : SECTIONS;

  const [connected, setConnected] = useState({}); // { make:true, hubspot:false, ... }
  const [busy, setBusy] = useState({});

  // Shopify connect needs the store's *.myshopify.com domain → collect it in a
  // small modal before redirecting into OAuth.
  const [shopifyOpen, setShopifyOpen] = useState(false);
  const [shopInput, setShopInput] = useState("");

  // Fetch status for one platform.
  const refreshStatus = useCallback(
    async (key) => {
      if (!userId) return;
      try {
        // userId passed as query param (no custom header → no CORS preflight).
        // Make/Zapier are per-agent → include agentId so the backend reports
        // status for THIS agent. HubSpot is user-level → no agentId.
        const agentParam =
          (key === "make" || key === "zapier") && agentId
            ? `&agentId=${encodeURIComponent(agentId)}`
            : "";
        const res = await fetch(
          `${API_BASE}/integration/connection-status?platform=${key}&userId=${encodeURIComponent(
            userId
          )}${agentParam}`
        );
        if (!res.ok) {
          console.error(`[integrations] ${key} status HTTP ${res.status}`);
          return;
        }
        const data = await res.json();
        console.log(`[integrations] ${key} status →`, data);
        setConnected((s) => ({ ...s, [key]: !!data.connected }));
      } catch (e) {
        console.error(`[integrations] ${key} status failed:`, e);
      }
    },
    [userId, agentId]
  );

  // On load, check status for every available platform.
  useEffect(() => {
   // mcpOnly: baaki platforms dikhte hi nahi, unka status mangwana bekaar.
   if (mcpOnly) return;
   PLATFORMS.filter(
     (p) => p.status === "available" && p.key !== "make" && p.key !== "zapier",
   ).forEach((p) => refreshStatus(p.key));
  }, [refreshStatus, mcpOnly]);

  const handleConnect = (platform) => {
    if (!userId) {
      alert("Please log in first.");
      return;
    }
    if (platform.key === "hubspot") {
      window.location.href = getHubspotAuthUrl(userId);
      return;
    }
    if (platform.key === "shopify") {
      setShopInput("");
      setShopifyOpen(true); // collect the store domain, then redirect (below)
      return;
    }
    // NOTE: Zapier & Make have NO app-side connect — their cards show a
    // "Learn more" link (see renderButton). HubSpot is the only app-side connect.
  };

  // Shopify uses the SAME backend install endpoint as the App Store flow, but we
  // ALSO pass the logged-in user's JWT as `rexpt_user` so the OAuth callback links
  // the store to THIS account (no email-matching / no signup detour).
  const startShopifyConnect = () => {
    let shop = (shopInput || "")
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/\/.*$/, "");
    if (!shop) return;
    if (!shop.endsWith(".myshopify.com")) shop = `${shop.split(".")[0]}.myshopify.com`;
    const token = localStorage.getItem("token") || "";
    window.location.href = `${API_BASE}/integration/shopify/install?shop=${encodeURIComponent(
      shop
    )}&rexpt_user=${encodeURIComponent(token)}`;
  };

  const handleDisconnect = async (platform) => {
    if (!userId) return;
    setBusy((b) => ({ ...b, [platform.key]: true }));
    try {
      // Make/Zapier are per-agent → send agentId so only THIS agent is
      // disconnected (others keep flowing). HubSpot is user-level → no agentId.
      const body = { userId, platform: platform.key };
      if ((platform.key === "make" || platform.key === "zapier") && agentId) {
        body.agentId = agentId;
      }
      await fetch(`${API_BASE}/integration/disconnect`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      await refreshStatus(platform.key);
    } catch (e) {
      console.error(`[integrations] ${platform.key} disconnect failed:`, e);
    } finally {
      setBusy((b) => ({ ...b, [platform.key]: false }));
    }
  };

  const renderButton = (p) => {
    // MCP: full management screen of its own (add servers, discover + enable
    // tools per agent). Carry the agentId so tools link to THIS agent.
    if (p.key === "mcp") {
      return (
        <button
          type="button"
          className={styles.connectBtn}
          onClick={() => navigate("/mcp-integration", { state: { agentId } })}
        >
          Manage
        </button>
      );
    }
    // Zapier & Make: no app-side connect/disconnect. The user connects from their
    // side; here we only offer a "Learn more" link (regardless of status).
    if (p.key === "zapier" || p.key === "make") {
      return (
        <button
          type="button"
          className={`${styles.connectBtn} ${styles.learnBtn}`}
          onClick={() =>
            window.open(GUIDE_URLS[p.key], "_blank", "noopener,noreferrer")
          }
        >
          Learn more
        </button>
      );
    }
    if (p.status === "coming_soon") {
      return (
        <button type="button" className={styles.connectBtn} disabled>
          Coming soon
        </button>
      );
    }
    const isConnected = connected[p.key];
    const isBusy = busy[p.key];

    if (isConnected) {
      return (
        <button
          type="button"
          className={styles.connectBtn}
          disabled={isBusy}
          onClick={() => handleDisconnect(p)}
        >
          {isBusy ? "Disconnecting…" : "Disconnect"}
        </button>
      );
    }
    return (
      <button
        type="button"
        className={styles.connectBtn}
        disabled={isBusy}
        onClick={() => handleConnect(p)}
      >
        Connect
      </button>
    );
  };

  return (
    <div className={styles.page}>
      {/* Sticky wrapper — scroll pe bhi back arrow hamesha dikhe */}
      <div className={styles.Forsticky}>
        <HeaderBar title="Integrations" backgroundColor="#6524EB" color="#fff" />
      </div>

      <div className={styles.body}>
        {/* Intro */}
        <div className={styles.introRow}>
          <span className={styles.introIcon}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M9 15l6-6M10.5 6.5l1-1a4 4 0 0 1 6 6l-1 1M13.5 17.5l-1 1a4 4 0 0 1-6-6l1-1"
                stroke="#6524EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <div>
            <h2 className={styles.introTitle}>
              Connect Rexpt AI with the tools you already use.
            </h2>
            <p className={styles.introSub}>More integrations are on the way.</p>
          </div>
        </div>

        {sections.map((section) => (
          <div key={section.title} className={styles.section}>
            <div className={styles.sectionHead}>
              <span className={styles.sectionIcon}>{section.icon}</span>
              <div>
                <h3 className={styles.sectionTitle}>{section.title}</h3>
                <p className={styles.sectionSub}>{section.subtitle}</p>
              </div>
            </div>

            <div className={styles.sectionCards}>
              {section.keys.map((key) => {
                const p = PLATFORMS.find((pl) => pl.key === key);
                if (!p) return null;
                return (
                  <div key={p.key} className={styles.card}>
                    <div className={styles.iconWrap}>
                      <img src={p.icon} alt={p.name} />
                    </div>
                    <div className={styles.cardText}>
                      <div className={styles.cardNameRow}>
                        <h4 className={styles.cardName}>{p.name}</h4>
                        {p.status === "available" &&
                          connected[p.key] &&
                          p.key !== "make" &&
                          p.key !== "zapier" && (
                            <span className={styles.badge}>Connected</span>
                          )}
                      </div>
                      <p className={styles.cardDesc}>{p.description}</p>
                    </div>
                    <div className={styles.actionCol}>
                      {renderButton(p)}
                      {HELP_CONTENT[p.key] && (
                        <div className={styles.tipUnderBtn}>
                          <Tooltip content={HELP_CONTENT[p.key]} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Request footer */}
        
      </div>

      {shopifyOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
          onClick={() => setShopifyOpen(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: 24,
              width: "min(92vw, 360px)",
              boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 8px", color: "#6524EB" }}>Connect Shopify</h3>
            <p style={{ margin: "0 0 16px", fontSize: 14, color: "#555" }}>
              Enter your Shopify store address to start the secure install.
            </p>
            <input
              type="text"
              value={shopInput}
              onChange={(e) => setShopInput(e.target.value)}
              placeholder="your-store.myshopify.com"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") startShopifyConnect();
              }}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid #ddd",
                fontSize: 15,
                marginBottom: 16,
              }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={() => setShopifyOpen(false)}
                style={{
                  flex: 1,
                  padding: 12,
                  borderRadius: 10,
                  border: "1px solid #ddd",
                  background: "#fff",
                  color: "#555",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={startShopifyConnect}
                disabled={!shopInput.trim()}
                style={{
                  flex: 1,
                  padding: 12,
                  borderRadius: 10,
                  border: "none",
                  background: "#6524EB",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                  opacity: shopInput.trim() ? 1 : 0.6,
                }}
              >
                Connect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsPage;
